import { z } from 'zod';

/**
 * Mercury international wires (any country other than the US). Mercury's API (docs.mercury.com, checked 2026-10):
 *  - sends international wires in USD only, through `POST /account/{id}/request-send-money` with
 *    `paymentMethod: "internationalWire"` and a required `purpose` — never by direct send — so every international wire
 *    waits for approval in the Mercury dashboard;
 *  - can't create an international-wire recipient by API (`POST /recipients` has no international field): the recipient
 *    is added in the Mercury dashboard or through a Mercury recipient invite, and is then read back by API
 *    (`internationalWireRoutingInfo`: iban, swiftCode, bankDetails, countrySpecific, …).
 * We collect and verify the full details here (encrypted, masked, second-person verification as for all bank details)
 * and, on verification, link them to the Mercury recipient whose SWIFT code and IBAN / account number match exactly.
 */

/** Every ISO 3166-1 alpha-2 country code (names come from Intl.DisplayNames in the browser). */
export const ISO_COUNTRIES = [
  'AD', 'AE', 'AF', 'AG', 'AI', 'AL', 'AM', 'AO', 'AQ', 'AR', 'AS', 'AT', 'AU', 'AW', 'AX', 'AZ', 'BA', 'BB', 'BD', 'BE', 'BF', 'BG', 'BH', 'BI', 'BJ', 'BL', 'BM', 'BN', 'BO', 'BQ', 'BR', 'BS', 'BT', 'BV', 'BW', 'BY', 'BZ',
  'CA', 'CC', 'CD', 'CF', 'CG', 'CH', 'CI', 'CK', 'CL', 'CM', 'CN', 'CO', 'CR', 'CU', 'CV', 'CW', 'CX', 'CY', 'CZ', 'DE', 'DJ', 'DK', 'DM', 'DO', 'DZ', 'EC', 'EE', 'EG', 'EH', 'ER', 'ES', 'ET', 'FI', 'FJ', 'FK', 'FM', 'FO', 'FR',
  'GA', 'GB', 'GD', 'GE', 'GF', 'GG', 'GH', 'GI', 'GL', 'GM', 'GN', 'GP', 'GQ', 'GR', 'GS', 'GT', 'GU', 'GW', 'GY', 'HK', 'HM', 'HN', 'HR', 'HT', 'HU', 'ID', 'IE', 'IL', 'IM', 'IN', 'IO', 'IQ', 'IR', 'IS', 'IT', 'JE', 'JM', 'JO', 'JP',
  'KE', 'KG', 'KH', 'KI', 'KM', 'KN', 'KP', 'KR', 'KW', 'KY', 'KZ', 'LA', 'LB', 'LC', 'LI', 'LK', 'LR', 'LS', 'LT', 'LU', 'LV', 'LY', 'MA', 'MC', 'MD', 'ME', 'MF', 'MG', 'MH', 'MK', 'ML', 'MM', 'MN', 'MO', 'MP', 'MQ', 'MR', 'MS', 'MT',
  'MU', 'MV', 'MW', 'MX', 'MY', 'MZ', 'NA', 'NC', 'NE', 'NF', 'NG', 'NI', 'NL', 'NO', 'NP', 'NR', 'NU', 'NZ', 'OM', 'PA', 'PE', 'PF', 'PG', 'PH', 'PK', 'PL', 'PM', 'PN', 'PR', 'PS', 'PT', 'PW', 'PY', 'QA', 'RE', 'RO', 'RS', 'RU', 'RW',
  'SA', 'SB', 'SC', 'SD', 'SE', 'SG', 'SH', 'SI', 'SJ', 'SK', 'SL', 'SM', 'SN', 'SO', 'SR', 'SS', 'ST', 'SV', 'SX', 'SY', 'SZ', 'TC', 'TD', 'TF', 'TG', 'TH', 'TJ', 'TK', 'TL', 'TM', 'TN', 'TO', 'TR', 'TT', 'TV', 'TW', 'TZ', 'UA', 'UG',
  'UM', 'US', 'UY', 'UZ', 'VA', 'VC', 'VE', 'VG', 'VI', 'VN', 'VU', 'WF', 'WS', 'XK', 'YE', 'YT', 'ZA', 'ZM', 'ZW',
] as const;
const ISO_SET = new Set<string>(ISO_COUNTRIES);
export const isIsoCountry = (c: string) => ISO_SET.has(c.trim().toUpperCase());

/** IBAN length per country (SWIFT IBAN registry). */
export const IBAN_LENGTHS: Record<string, number> = {
  AD: 24, AE: 23, AL: 28, AT: 20, AZ: 28, BA: 20, BE: 16, BG: 22, BH: 22, BI: 27, BR: 29, BY: 28, CH: 21, CR: 22, CY: 28, CZ: 24, DE: 22, DJ: 27, DK: 18, DO: 28, EE: 20, EG: 29, ES: 24, FI: 18, FK: 18, FO: 18, FR: 27,
  GB: 22, GE: 22, GI: 23, GL: 18, GR: 27, GT: 28, HR: 21, HU: 28, IE: 22, IL: 23, IQ: 23, IS: 26, IT: 27, JO: 30, KW: 30, KZ: 20, LB: 28, LC: 32, LI: 21, LT: 20, LU: 20, LV: 21, LY: 25, MC: 27, MD: 24, ME: 22, MK: 19,
  MN: 20, MR: 27, MT: 31, MU: 30, NI: 28, NL: 18, NO: 15, OM: 23, PK: 24, PL: 28, PS: 29, PT: 25, QA: 29, RO: 24, RS: 22, RU: 33, SA: 24, SC: 31, SD: 18, SE: 24, SI: 19, SK: 24, SM: 27, SO: 23, ST: 25, SV: 28, TL: 23, TN: 24,
  TR: 26, UA: 29, VA: 22, VG: 24, XK: 20, YE: 30,
};
/** Countries whose banks are paid by IBAN only (EEA, UK, Switzerland and the Gulf / Middle East IBAN countries). */
export const IBAN_REQUIRED = new Set([
  'AD', 'AT', 'BE', 'BG', 'CH', 'CY', 'CZ', 'DE', 'DK', 'EE', 'ES', 'FI', 'FR', 'GB', 'GI', 'GR', 'HR', 'HU', 'IE', 'IS', 'IT', 'LI', 'LT', 'LU', 'LV', 'MC', 'MT', 'NL', 'NO', 'PL', 'PT', 'RO', 'SE', 'SI', 'SK', 'SM', 'VA',
  'AE', 'BH', 'IL', 'JO', 'KW', 'LB', 'QA', 'SA', 'TR',
]);

export const compact = (v: string | null | undefined) => (v ?? '').replace(/[\s-]/g, '').toUpperCase();

/** IBAN check: known country, its length, and the ISO 7064 mod-97 check digits. */
export function ibanValid(raw: string): boolean {
  const v = compact(raw);
  if (!/^[A-Z]{2}\d{2}[A-Z0-9]+$/.test(v)) return false;
  const len = IBAN_LENGTHS[v.slice(0, 2)];
  if (!len || v.length !== len) return false;
  const moved = v.slice(4) + v.slice(0, 4);
  let rem = 0;
  for (const ch of moved) {
    const n = ch >= 'A' ? String(ch.charCodeAt(0) - 55) : ch;
    for (const d of n) rem = (rem * 10 + Number(d)) % 97;
  }
  return rem === 1;
}
/** Looks like an IBAN (2 letters of an IBAN country + 2 digits): then it must be a valid one. */
export const looksLikeIban = (raw: string) => {
  const v = compact(raw);
  return /^[A-Z]{2}\d{2}/.test(v) && !!IBAN_LENGTHS[v.slice(0, 2)];
};

/** SWIFT / BIC: 4 letters (bank), 2 letters (country), 2 letters or digits (location), optional 3 (branch). */
export const swiftValid = (raw: string) => /^[A-Z]{6}[A-Z0-9]{2}([A-Z0-9]{3})?$/.test(compact(raw));

/** Mexican CLABE: 18 digits, the last a check digit (weights 3, 7, 1). */
export function clabeValid(raw: string): boolean {
  const v = compact(raw);
  if (!/^\d{18}$/.test(v)) return false;
  const w = [3, 7, 1];
  const sum = v
    .slice(0, 17)
    .split('')
    .reduce((s, d, i) => s + ((Number(d) * w[i % 3]) % 10), 0);
  return (10 - (sum % 10)) % 10 === Number(v[17]);
}

/** Mercury's purpose categories for a wire (`purpose.simple.category`). */
export const MERCURY_PURPOSE_CATEGORIES = ['employee', 'contractor', 'vendor', 'forGoodsOrServices', 'expenses', 'landlord', 'subsidiary', 'transferToMyExternalAccount', 'familyMemberOrFriend', 'travel', 'other'] as const;
export type MercuryPurposeCategory = (typeof MERCURY_PURPOSE_CATEGORIES)[number];
export const MERCURY_PURPOSE_LABELS: Record<MercuryPurposeCategory, string> = {
  employee: 'Salary (employee)',
  contractor: 'Contractor / representative fee',
  vendor: 'Vendor payment',
  forGoodsOrServices: 'Goods or services',
  expenses: 'Expense reimbursement',
  landlord: 'Rent (landlord)',
  subsidiary: 'Payment to a subsidiary',
  transferToMyExternalAccount: 'Transfer to own account',
  familyMemberOrFriend: 'Family member or friend',
  travel: 'Travel',
  other: 'Other',
};
/** Mercury requires `additionalInfo` (the name / description) for these categories and refuses it for most others. */
export const MERCURY_PURPOSE_NEEDS_INFO = new Set<string>(['vendor', 'contractor', 'other', 'subsidiary']);

/** Country-specific bank codes, keyed as Mercury names them in `countrySpecific` where it has them. */
export interface MercuryCountryField {
  key: string;
  label: string;
  required: boolean;
  /** Checked on the value without spaces / dashes. */
  pattern: string | null;
  example?: string;
  help?: string;
}
const legalId = (help: string): MercuryCountryField[] => [{ key: 'legalId', label: "Recipient's tax / national ID", required: true, pattern: '[A-Za-z0-9.]{4,30}', help }];
export const MERCURY_COUNTRY_FIELDS: Record<string, MercuryCountryField[]> = {
  IN: [{ key: 'ifscCode', label: 'IFSC code', required: true, pattern: '[A-Z]{4}0[A-Z0-9]{6}', example: 'HDFC0000123', help: '11 characters: 4 letters, 0, then 6 letters or digits' }],
  GB: [{ key: 'sortCode', label: 'Sort code (6 digits)', required: true, pattern: '\\d{6}', example: '40-47-84' }],
  CA: [
    { key: 'bankCode', label: 'Institution number (3 digits)', required: true, pattern: '\\d{3}', example: '003' },
    { key: 'transitNumber', label: 'Transit (branch) number (5 digits)', required: true, pattern: '\\d{5}', example: '12345' },
  ],
  AU: [{ key: 'bsbCode', label: 'BSB (6 digits)', required: true, pattern: '\\d{6}', example: '062-000' }],
  CN: [{ key: 'cnaps', label: 'CNAPS code (12 digits)', required: false, pattern: '\\d{12}', help: 'Optional; speeds up CNY delivery in China' }],
  ZA: [{ key: 'branchCode', label: 'Branch code (6 digits)', required: true, pattern: '\\d{6}', example: '250655' }],
  PH: [{ key: 'routingNumber', label: 'Bank routing number (BRSTN, 9 digits)', required: true, pattern: '\\d{9}' }],
  BR: legalId('CPF (individual) or CNPJ (business)'),
  CL: legalId('RUT'),
  CO: legalId('NIT or cédula'),
  KZ: legalId('IIN / BIN'),
  PY: legalId('RUC or cédula'),
  DO: legalId('Cédula or RNC'),
  HN: legalId('RTN'),
  PK: legalId('CNIC or NTN'),
  RU: [{ key: 'inn', label: 'INN (10 or 12 digits)', required: true, pattern: '\\d{10}|\\d{12}' }],
};
export const mercuryCountryFields = (country: string) => MERCURY_COUNTRY_FIELDS[country.toUpperCase()] ?? [];

/** What the account-number field is called for a country (IBAN, CLABE, or account number). */
export function mercuryAccountField(country: string): { label: string; help: string | null; example: string | null } {
  const c = country.toUpperCase();
  if (c === 'MX') return { label: 'CLABE (18 digits)', help: 'Mexican wires are paid to the 18-digit CLABE', example: '032180000118359719' };
  if (IBAN_REQUIRED.has(c)) return { label: 'IBAN', help: `${c} accounts are paid by IBAN`, example: c === 'GB' ? 'GB29 NWBK 6016 1331 9268 19' : c === 'DE' ? 'DE89 3704 0044 0532 0130 00' : null };
  return { label: IBAN_LENGTHS[c] ? 'IBAN or account number' : 'Account number', help: null, example: null };
}

const optText = (max: number) => z.string().trim().max(max).nullish();
/** International-wire details kept with Mercury bank details for a non-US account (stored encrypted, like all). */
export const mercuryIntlInput = z.object({
  /** Bank's city and state / province, e.g. "Kathmandu, Bagmati". */
  bankCityState: z.string().trim().max(120).default(''),
  purposeCategory: z.string().trim().max(40).default(''),
  /** Country-specific bank codes (IFSC, sort code, BSB, transit / institution, CNAPS, branch code, legal ID, …). */
  countrySpecific: z.record(z.string().trim().max(40), z.string().trim().max(40)).nullish(),
  /** Intermediary (correspondent) bank, when the recipient's bank asks for one. */
  correspondent: z.object({ bankName: optText(140), swiftCode: optText(20), routingNumber: optText(20) }).nullish(),
  phone: optText(40),
});
export type MercuryIntlInput = z.infer<typeof mercuryIntlInput>;

/** Form paths (bank-details form) of the international fields that are not standard beneficiary paths. */
export const MERCURY_INTL_PATHS = {
  bankCityState: 'mercury.bank_city_state',
  purposeCategory: 'mercury.purpose_category',
  countrySpecific: (key: string) => `mercury.country_specific.${key}`,
  correspondentBankName: 'mercury.correspondent.bank_name',
  correspondentSwift: 'mercury.correspondent.swift_code',
  correspondentRouting: 'mercury.correspondent.routing_number',
  phone: 'beneficiary.additional_info.personal_mobile_number',
} as const;

/** The international details from the bank-details form values. */
export function mercuryIntlFromFields(country: string, v: Record<string, string>): MercuryIntlInput {
  const g = (p: string) => (v[p] ?? '').trim();
  const countrySpecific = Object.fromEntries(mercuryCountryFields(country).map((f) => [f.key, compact(g(MERCURY_INTL_PATHS.countrySpecific(f.key)))]).filter(([, x]) => x));
  const correspondent = { bankName: g(MERCURY_INTL_PATHS.correspondentBankName) || null, swiftCode: compact(g(MERCURY_INTL_PATHS.correspondentSwift)) || null, routingNumber: compact(g(MERCURY_INTL_PATHS.correspondentRouting)) || null };
  return {
    bankCityState: g(MERCURY_INTL_PATHS.bankCityState),
    purposeCategory: g(MERCURY_INTL_PATHS.purposeCategory),
    countrySpecific: Object.keys(countrySpecific).length ? countrySpecific : null,
    correspondent: correspondent.bankName || correspondent.swiftCode || correspondent.routingNumber ? correspondent : null,
    phone: g(MERCURY_INTL_PATHS.phone) || null,
  };
}

/**
 * Checks Mercury international-wire details. Returns errors keyed by: bankCountry, accountNumber, swiftCode, bankName,
 * intl.bankCityState, intl.purposeCategory, intl.countrySpecific.<key>, intl.correspondent.swiftCode,
 * intl.correspondent.routingNumber.
 */
export function mercuryIntlErrors(v: { bankCountry: string; accountNumber: string; swiftCode?: string | null; bankName?: string | null; intl?: MercuryIntlInput | null }): Record<string, string> {
  const e: Record<string, string> = {};
  const country = v.bankCountry.trim().toUpperCase();
  if (!isIsoCountry(country)) e.bankCountry = 'Choose the bank country';
  else if (country === 'US') e.bankCountry = 'US accounts are paid by ACH, real-time payment or domestic wire';
  const swift = compact(v.swiftCode);
  if (!swift) e.swiftCode = 'SWIFT / BIC code is required for an international wire';
  else if (!swiftValid(swift)) e.swiftCode = 'A SWIFT / BIC code is 8 or 11 letters and digits (e.g. NARBNPKA or DEUTDEFF500)';
  const acct = compact(v.accountNumber);
  if (!acct) e.accountNumber = `${mercuryAccountField(country).label} is required`;
  else if (country === 'MX') {
    if (!clabeValid(acct)) e.accountNumber = "This CLABE isn't valid (18 digits; the check digit doesn't match)";
  } else if (looksLikeIban(acct)) {
    if (!ibanValid(acct)) e.accountNumber = "This IBAN isn't valid (length or check digits don't match) — check it";
    else if (acct.slice(0, 2) !== country) e.accountNumber = `This IBAN is for a bank in ${acct.slice(0, 2)}, not ${country}`;
  } else if (IBAN_REQUIRED.has(country)) e.accountNumber = `Enter the IBAN: ${country} accounts are paid by IBAN`;
  else if (!/^[A-Z0-9]{4,34}$/.test(acct)) e.accountNumber = 'Enter the account number (4–34 letters or digits)';
  if (!(v.bankName ?? '').trim()) e.bankName = 'Bank name is required';
  const intl = v.intl ?? null;
  if (!intl?.bankCityState?.trim()) e['intl.bankCityState'] = "Bank city and state / province are required";
  if (!intl?.purposeCategory || !(MERCURY_PURPOSE_CATEGORIES as readonly string[]).includes(intl.purposeCategory)) e['intl.purposeCategory'] = 'Choose the purpose of the payments (required for international wires)';
  for (const f of mercuryCountryFields(country)) {
    const val = compact(intl?.countrySpecific?.[f.key]);
    if (!val) {
      if (f.required) e[`intl.countrySpecific.${f.key}`] = `${f.label} is required for ${country}`;
      continue;
    }
    if (f.pattern && !new RegExp(`^(?:${f.pattern})$`).test(val)) e[`intl.countrySpecific.${f.key}`] = `${f.label} doesn't look right${f.example ? ` (e.g. ${f.example})` : ''}`;
  }
  // A UK IBAN carries the sort code (characters 9–14): both must name the same branch.
  const sort = compact(intl?.countrySpecific?.sortCode);
  if (country === 'GB' && sort && /^\d{6}$/.test(sort) && ibanValid(acct) && acct.slice(8, 14) !== sort) e['intl.countrySpecific.sortCode'] = `The sort code doesn't match the IBAN (${acct.slice(8, 10)}-${acct.slice(10, 12)}-${acct.slice(12, 14)})`;
  const cs = compact(intl?.correspondent?.swiftCode);
  if (cs && !swiftValid(cs)) e['intl.correspondent.swiftCode'] = 'The intermediary bank SWIFT code is 8 or 11 letters and digits';
  const cr = compact(intl?.correspondent?.routingNumber);
  if (cr && !/^\d{9}$/.test(cr)) e['intl.correspondent.routingNumber'] = 'The intermediary bank routing number (ABA) is 9 digits';
  return e;
}

/** Form path of each mercuryIntlErrors key (for showing errors next to the form fields). */
export function mercuryIntlErrorPath(key: string): string {
  if (key === 'bankCountry') return 'beneficiary.bank_details.bank_country_code';
  if (key === 'accountNumber') return 'beneficiary.bank_details.account_number';
  if (key === 'swiftCode') return 'beneficiary.bank_details.swift_code';
  if (key === 'bankName') return 'beneficiary.bank_details.bank_name';
  if (key === 'intl.bankCityState') return MERCURY_INTL_PATHS.bankCityState;
  if (key === 'intl.purposeCategory') return MERCURY_INTL_PATHS.purposeCategory;
  if (key.startsWith('intl.countrySpecific.')) return MERCURY_INTL_PATHS.countrySpecific(key.slice('intl.countrySpecific.'.length));
  if (key === 'intl.correspondent.swiftCode') return MERCURY_INTL_PATHS.correspondentSwift;
  if (key === 'intl.correspondent.routingNumber') return MERCURY_INTL_PATHS.correspondentRouting;
  return key;
}

/** Shown wherever a Mercury international wire is chosen or reviewed. */
export const MERCURY_INTL_NOTE = 'Sent as a USD international wire; the receiving bank converts. International wire fee may apply (Mercury).';
export const MERCURY_INTL_APPROVAL_NOTE = 'Mercury sends international wires only as approval requests: someone with send-money permission approves each one in the Mercury dashboard ("Awaiting bank approval" here until then).';
/** How a Mercury international recipient is set up (its API can't create one with bank details). */
export const MERCURY_INTL_RECIPIENT_HELP =
  "Mercury's API can't create international-wire recipients: add the recipient in the Mercury dashboard (Payments → Recipients → International wire) with exactly these details, or send them a Mercury invite to enter their own details. When the details are verified here they are linked to the Mercury recipient with the same SWIFT code and IBAN / account number.";
